import { caveat, loadFont } from '@aline/fonts/client';

interface FramedSigPreviewOptions {
  fallbackText: string;
  hPx: number;
  placeholderColor: string;
  previewInfo: {
    textOrDataUrl: string;
    type: 'text' | 'dataUrl';
  };
  wPx: number;
}

export async function drawFramedSigPreview(
  canvas: HTMLCanvasElement,
  options: FramedSigPreviewOptions
): Promise<void> {
  const {
    fallbackText,
    hPx,
    placeholderColor,
    previewInfo: { textOrDataUrl, type },
    wPx,
  } = options;
  // Idempotent — safe to call multiple times.
  await loadFont(caveat, { family: 'Caveat' });

  const lineThickness = 2;
  const signaturePreviewX = 20;
  const signaturePreviewRightPadding = 8;
  const signaturePreviewHeightPx = 24;
  const signaturePreviewMaxWidth = wPx - (signaturePreviewX + signaturePreviewRightPadding);
  const ctx = createHiResCtx(canvas, { hPx, wPx });
  if (!ctx) return;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.lineWidth = lineThickness;

  ctx.beginPath();
  ctx.arc(12, 12, 10, degreesToRadians(180), degreesToRadians(270));
  ctx.moveTo(lineThickness, 12);
  ctx.lineTo(lineThickness, 18);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(12, lineThickness);
  ctx.lineTo(100, lineThickness);
  ctx.stroke();

  const bottomFontSizePx = 10;
  const bottomArcY = hPx - 10 - bottomFontSizePx / 2;
  ctx.beginPath();
  ctx.arc(12, bottomArcY, 10, degreesToRadians(90), degreesToRadians(180));
  ctx.moveTo(lineThickness, bottomArcY);
  ctx.lineTo(lineThickness, bottomArcY - 6);
  ctx.stroke();

  const bottomLineY = hPx - bottomFontSizePx / 2;
  const endBottomLineX = 60;
  ctx.beginPath();
  ctx.moveTo(12, bottomLineY);
  ctx.lineTo(endBottomLineX, bottomLineY);
  ctx.stroke();

  switch (type) {
    case 'text': {
      const previewText = textOrDataUrl || fallbackText;
      ctx.font = `${signaturePreviewHeightPx}px Caveat`;
      ctx.textBaseline = 'middle';
      ctx.fillText(previewText, signaturePreviewX, hPx / 2, signaturePreviewMaxWidth);
      break;
    }
    case 'dataUrl': {
      await new Promise<void>(resolve => {
        const img = new Image();
        img.src = textOrDataUrl;
        img.addEventListener(
          'load',
          () => {
            ctx.drawImage(
              img,
              signaturePreviewX,
              hPx / 2 - signaturePreviewHeightPx / 2,
              signaturePreviewMaxWidth,
              signaturePreviewHeightPx
            );
            resolve();
          },
          false
        );
        img.addEventListener('error', () => resolve(), false);
      });
      break;
    }
  }

  ctx.font = '10px Arial';
  ctx.textBaseline = 'bottom';
  ctx.fillStyle = placeholderColor;
  ctx.fillText('Signed via Aline', endBottomLineX + 8, hPx);
}

export async function getCanvasBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise(resolve => {
    canvas.toBlob(resolve);
  });
}

/**
 * Creates a high-resolution 2D context for the given canvas, scaled by
 * devicePixelRatio (× the current visualViewport pinch scale) so text and
 * lines stay crisp on retina displays and during pinch zoom. Callers should
 * redraw whenever `visualViewport.scale` changes — see `useFramedPreview`
 * and `usePageCanvas` for the listener pattern.
 */
function createHiResCtx(
  canvas: HTMLCanvasElement,
  options: { hPx: number; wPx: number }
): CanvasRenderingContext2D | null {
  const { hPx, wPx } = options;
  const dpr = window.devicePixelRatio * (window.visualViewport?.scale ?? 1);
  canvas.width = wPx * dpr;
  canvas.height = hPx * dpr;
  canvas.style.width = `${wPx}px`;
  canvas.style.height = `${hPx}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.scale(dpr, dpr);

  return ctx;
}

function degreesToRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}
